import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRound, Loader2, Check, CircleAlert, Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch, apiBase } from '@/lib/api';

interface StripeCfg { publishable_key: string; secret_set: boolean; webhook_set: boolean }

const WEBHOOK_EVENTS = 'customer.subscription.*, invoice.*, checkout.session.completed';

// PlatformStripe — configuração das chaves do Stripe da PLATAFORMA (geridas no admin, não no
// .env). As chaves secret/webhook são gravadas criptografadas (platform_config) e aplicadas em
// runtime, sem restart. Só o dono da plataforma vê esta tela (gate no backend: requirePlatformAdmin).
export function PlatformStripe() {
  const qc = useQueryClient();
  const [secret, setSecret] = useState('');
  const [webhook, setWebhook] = useState('');
  const [pk, setPk] = useState('');
  const [pkDirty, setPkDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const cfg = useQuery({
    queryKey: ['platform-stripe'],
    queryFn: async (): Promise<StripeCfg> => {
      const r = await apiFetch('GET', '/platform/stripe');
      return (r.ok ? r.data : { publishable_key: '', secret_set: false, webhook_set: false }) as StripeCfg;
    },
  });

  const webhookURL = `${apiBase()}/billing/webhook`;
  const copy = (v: string) => { navigator.clipboard?.writeText(v); toast.success('Copiado.'); };

  const save = async () => {
    const publishable = pkDirty ? pk : (cfg.data?.publishable_key ?? '');
    if (!secret && !webhook && publishable === (cfg.data?.publishable_key ?? '')) {
      toast.info('Nada para salvar.'); return;
    }
    setBusy(true);
    try {
      // secret/webhook vazios = mantêm o atual; publishable é sempre gravada.
      const r = await apiFetch('PUT', '/platform/stripe', {
        secret_key: secret, webhook_secret: webhook, publishable_key: publishable,
      });
      if (r.ok) {
        toast.success('Configuração do Stripe salva. Aplicada em runtime (sem restart). 🎉');
        setSecret(''); setWebhook(''); setPkDirty(false);
        qc.invalidateQueries({ queryKey: ['platform-stripe'] });
        qc.invalidateQueries({ queryKey: ['entitlements'] });
      } else {
        toast.error('Não foi possível salvar. Confira as chaves e tente novamente.');
      }
    } finally { setBusy(false); }
  };

  if (cfg.isLoading || !cfg.data) return <Skeleton className="h-96 w-full" />;
  const d = cfg.data;
  const live = d.publishable_key.startsWith('pk_live') || secret.startsWith('sk_live');
  const fullyConfigured = d.secret_set && d.webhook_set && !!d.publishable_key;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <Card>
        <CardHeader className="flex-row items-center gap-2">
          <KeyRound className="size-5 text-primary" />
          <CardTitle>Stripe da plataforma</CardTitle>
          {fullyConfigured
            ? <Badge variant="success"><Check className="mr-1 size-3" /> Configurado{live ? ' · LIVE' : ' · teste'}</Badge>
            : <Badge variant="secondary"><CircleAlert className="mr-1 size-3" /> Incompleto</Badge>}
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">
            As chaves do Stripe ficam aqui (criptografadas no banco), não no servidor. Trocar e salvar
            aplica na hora, sem reiniciar. Sem elas, assinar o Pro e o checkout de add-ons ficam indisponíveis.
          </p>

          <div className="flex flex-col gap-2">
            <Label className="flex items-center justify-between">
              <span>Secret key <span className="font-mono text-xs text-muted-foreground">(sk_…)</span></span>
              {d.secret_set && <Badge variant="success" className="text-[0.65rem]">salva</Badge>}
            </Label>
            <Input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)}
              placeholder={d.secret_set ? '•••••••••• (deixe vazio para manter)' : 'sk_live_… ou sk_test_…'} />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="flex items-center justify-between">
              <span>Webhook signing secret <span className="font-mono text-xs text-muted-foreground">(whsec_…)</span></span>
              {d.webhook_set && <Badge variant="success" className="text-[0.65rem]">salvo</Badge>}
            </Label>
            <Input type="password" autoComplete="off" value={webhook} onChange={(e) => setWebhook(e.target.value)}
              placeholder={d.webhook_set ? '•••••••••• (deixe vazio para manter)' : 'whsec_…'} />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Publishable key <span className="font-mono text-xs text-muted-foreground">(pk_…)</span></Label>
            <Input autoComplete="off" value={pkDirty ? pk : d.publishable_key}
              onChange={(e) => { setPk(e.target.value); setPkDirty(true); }} placeholder="pk_live_… ou pk_test_…" />
            <p className="text-xs text-muted-foreground">Pública — usada pelo Stripe.js no navegador do cliente.</p>
          </div>

          <Button disabled={busy} onClick={save} className="self-start">
            {busy ? <Loader2 className="size-4 animate-spin" /> : null} Salvar configuração
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center gap-2">
          <ExternalLink className="size-5 text-primary" /><CardTitle>Webhook no Stripe</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">No painel do Stripe → Developers → Webhooks, aponte o endpoint para:</p>
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2">
            <code className="min-w-0 flex-1 truncate font-mono text-xs">{webhookURL}</code>
            <Button size="icon" variant="ghost" className="size-7 shrink-0" onClick={() => copy(webhookURL)} title="Copiar">
              <Copy className="size-4" />
            </Button>
          </div>
          <p className="text-muted-foreground">Eventos: <code className="font-mono text-xs">{WEBHOOK_EVENTS}</code>. O <em>signing secret</em> que o Stripe gera (whsec_) vai no campo acima.</p>
        </CardContent>
      </Card>
    </div>
  );
}
